import { createContext, useContext, useState, useCallback, ReactNode } from 'react';

interface SidebarContextType {
  isMobileSidebarOpen: boolean;
  openMobileSidebar: () => void;
  closeMobileSidebar: () => void;
  toggleMobileSidebar: () => void;
  isDesktopSidebarExpanded: boolean;
  toggleDesktopSidebar: () => void;
  collapseDesktopSidebar: () => void;
  expandDesktopSidebar: () => void;
}

const SidebarContext = createContext<SidebarContextType | undefined>(
  undefined,
);

interface SidebarProviderProps {
  children: ReactNode;
}

export function MobileSidebarProvider({ children }: SidebarProviderProps) {
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isDesktopSidebarExpanded, setIsDesktopSidebarExpanded] = useState(true);

  const openMobileSidebar = useCallback(() => {
    setIsMobileSidebarOpen(true);
  }, []);

  const closeMobileSidebar = useCallback(() => {
    setIsMobileSidebarOpen(false);
  }, []);

  const toggleMobileSidebar = useCallback(() => {
    setIsMobileSidebarOpen((prev) => !prev);
  }, []);

  const toggleDesktopSidebar = useCallback(() => {
    setIsDesktopSidebarExpanded((prev) => !prev);
  }, []);

  const collapseDesktopSidebar = useCallback(() => {
    setIsDesktopSidebarExpanded(false);
  }, []);

  const expandDesktopSidebar = useCallback(() => {
    setIsDesktopSidebarExpanded(true);
  }, []);

  return (
    <SidebarContext.Provider
      value={{
        isMobileSidebarOpen,
        openMobileSidebar,
        closeMobileSidebar,
        toggleMobileSidebar,
        isDesktopSidebarExpanded,
        toggleDesktopSidebar,
        collapseDesktopSidebar,
        expandDesktopSidebar,
      }}
    >
      {children}
    </SidebarContext.Provider>
  );
}

export function useMobileSidebar() {
  const context = useContext(SidebarContext);
  if (!context) {
    throw new Error('useMobileSidebar must be used within MobileSidebarProvider');
  }
  return context;
}
